import qs from 'qs'
import { AD_CHANNELS, CHANNEL_MEASURES } from '@/config/mlap/overviewMetrics'
import { MLAP_STAT_CARDS } from '@/config/mlap/statCards'

const DETAILS_PANELS = ['revenue', 'job', 'review', 'geogrid', 'competitor']

const PANEL_BY_ACTION = {
  'open-revenue-intel': 'revenue',
  'open-job-intel': 'job',
  'open-review-intel': 'review',
  'open-geogrid-intel': 'geogrid'
}

const CARDS = Object.fromEntries(MLAP_STAT_CARDS.map((card) => [card.key, card]))

// "Back to Overview" restores these params.
const OVERVIEW_PARAMS = ['date_range', 'territory_id', 'business_id', 'compare_mode', 'compare_date_from', 'compare_date_to', 'dashboard']

export const detailsPath = (pagePrefix) => `${pagePrefix}/details`

export function locationPageHref(pagePrefix, locationId, query = {}) {
  const search = qs.stringify(query, { skipNulls: true })
  return `${pagePrefix}/locations/${locationId}${search ? `?${search}` : ''}`
}

const channelCards = (field) => AD_CHANNELS.map((channel) => channel[field]).filter((key) => key && CARDS[key])

function costPerLeadCards(costPerLeadChannels) {
  const channels = AD_CHANNELS.filter((channel) => (costPerLeadChannels ? costPerLeadChannels.includes(channel.key) : channel.leads))
  return [...channels.map((channel) => channel.spend), ...channels.map((channel) => channel.leads)].filter((key) => key && CARDS[key])
}

// A paid total has no card on Details, so its channel cards stand in.
function detailsCardKeys(metricKey, costPerLeadChannels) {
  if (CARDS[metricKey]) return [metricKey]
  if (metricKey === 'paid_spend') return channelCards('spend')
  if (metricKey === 'paid_leads') return channelCards('leads')
  if (metricKey === 'paid_cost_per_lead') return costPerLeadCards(costPerLeadChannels)
  const channel = AD_CHANNELS.find((item) => item.costPerLead === metricKey)
  if (channel) return [channel.spend, channel.leads].filter((key) => key && CARDS[key])
  return []
}

export function detailsTargetForMetric(metricKey, { costPerLeadChannels = null } = {}) {
  const cards = detailsCardKeys(metricKey, costPerLeadChannels)
  const panel = cards.length === 1 ? PANEL_BY_ACTION[CARDS[cards[0]].clickActionId] || null : null
  return { panel, cards }
}

const unique = (keys) => [...new Set(keys)]

export function detailsTargetForWidget(widget, { costPerLeadChannels = null } = {}) {
  const options = widget?.options || {}
  switch (widget?.type) {
    case 'leaderboard':
    case 'territory_comparison':
      return options.metric ? detailsTargetForMetric(options.metric, { costPerLeadChannels }) : null
    case 'kpi_strip':
    case 'trend':
      return { panel: null, cards: unique((options.metrics || []).flatMap((key) => detailsCardKeys(key, costPerLeadChannels))) }
    case 'channel_mix': {
      const measure = CHANNEL_MEASURES[options.measure]
      if (!measure) return null
      const keys = options.measure === 'cost_per_lead' ? costPerLeadCards(costPerLeadChannels) : channelCards(measure.field)
      return { panel: null, cards: keys }
    }
    default:
      return null
  }
}

export function detailsHref(base, query, { target = null, dashboard = null } = {}) {
  const params = {
    ...query,
    from: 'overview',
    dashboard: dashboard || undefined,
    panel: target?.panel || undefined,
    card: target?.cards?.length ? target.cards.join(',') : undefined
  }
  const search = qs.stringify(params, { arrayFormat: 'brackets', skipNulls: true })
  return `${base}${search ? `?${search}` : ''}`
}

// The query is snake_case, as qs.parse gives it.
export function readDetailsDeepLink(query = {}) {
  const panel = DETAILS_PANELS.includes(query.panel) ? query.panel : null
  const locationId = typeof query.location_id === 'string' && query.location_id ? query.location_id : null
  const cards = String(query.card || '').split(',').map((key) => key.trim()).filter((key) => CARDS[key])
  const fromOverview = query.from === 'overview'
  const overviewQuery = fromOverview ?
    Object.fromEntries(OVERVIEW_PARAMS.filter((key) => query[key] != null && query[key] !== '').map((key) => [key, query[key]])) :
    null
  return {
    panel: panel === 'competitor' && !locationId ? null : panel,
    cards: unique(cards),
    locationId,
    fromOverview,
    overviewQuery
  }
}

export function overviewBackHref(projectPrefix, overviewQuery) {
  const search = qs.stringify(overviewQuery || {}, { arrayFormat: 'brackets', skipNulls: true })
  return `${projectPrefix}${search ? `?${search}` : ''}`
}

export function locationBackHref({ pagePrefix, detailsPath: path, params, fromOverview = false }) {
  if (fromOverview) return overviewBackHref(pagePrefix, params)
  const search = qs.stringify(params || {}, { arrayFormat: 'brackets', skipNulls: true })
  return search ? `${path}?${search}` : path
}

// Details keeps these params in its URL, so a reload keeps the way back.
export function persistentDeepLinkParams(deepLink) {
  if (!deepLink?.fromOverview) return {}
  return { from: 'overview', dashboard: deepLink.overviewQuery?.dashboard }
}
